import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Button } from '@euler/components/Button';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsAppActive } from '@euler/utils/hooks/useIsAppActive';
import { memo, useEffect, useRef, useState } from 'react';
import { Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from 'styled-components/native';

// seconds to wait before each automatic retry; the last one repeats.
const kAutoRetryDelays = [5, 10, 20, 30, 60];

const CloudOffIcon = memo(
  ({ size, color }: { size: number; color: string }) => (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="m2 2 20 20" />
      <Path d="M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193" />
      <Path d="M21.532 16.5A4.5 4.5 0 0 0 17 10h-1.79A7.008 7.008 0 0 0 10 5.07" />
    </Svg>
  ),
);

/**
 * Shown on app start when the user still holds a usable session but the
 * server could not be reached to confirm it, so they aren't dumped on the
 * login screen as if their session had ended.
 */
export const ServerUnreachableScreen = memo(
  ({ retrying, onRetry }: { retrying: boolean; onRetry: () => void }) => {
    // inlined `useAuthTheme`: importing it from ../hooks would form a cycle
    // through the auth context.
    const mode = usePresentationMode();
    const presentationTheme = usePresentationTheme();
    const normalTheme = useTheme();
    const theme =
      mode === 'presentation' ? presentationTheme.auth : normalTheme.auth;
    const { rpx } = useResponsiveContext();

    // retry immediately when the user comes back to the app, e.g. after
    // fixing their network in system settings.
    const isActive = useIsAppActive();
    const prevActive = useRef(isActive);
    useEffect(() => {
      if (isActive && !prevActive.current && !retrying) {
        onRetry();
      }
      prevActive.current = isActive;
    }, [isActive, onRetry, retrying]);

    // count finished attempts to back off the automatic retry.
    const [attempt, setAttempt] = useState(0);
    const wasRetrying = useRef(retrying);
    useEffect(() => {
      if (wasRetrying.current && !retrying) {
        setAttempt(x => x + 1);
      }
      wasRetrying.current = retrying;
    }, [retrying]);

    // count down to the next automatic retry; paused while a retry is in
    // flight or the app is in the background.
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
    useEffect(() => {
      if (retrying || !isActive) {
        setSecondsLeft(null);
        return;
      }
      setSecondsLeft(
        kAutoRetryDelays[Math.min(attempt, kAutoRetryDelays.length - 1)],
      );
      const timer = setInterval(() => {
        setSecondsLeft(x => (x == null ? null : Math.max(0, x - 1)));
      }, 1000);
      return () => clearInterval(timer);
    }, [attempt, isActive, retrying]);

    useEffect(() => {
      if (secondsLeft === 0) {
        onRetry();
      }
    }, [onRetry, secondsLeft]);

    return (
      <SafeAreaView
        css={`
          flex: 1;
          background-color: ${theme.mainPanelBgColor};
          align-items: center;
          justify-content: center;
        `}
      >
        <View
          css={`
            align-items: center;
            padding: 0 ${rpx(40)}px;
            max-width: ${rpx(420)}px;
          `}
        >
          <View
            css={`
              margin-bottom: ${rpx(24)}px;
            `}
          >
            <CloudOffIcon
              size={rpx(72)}
              color={theme.loginHeaderSubTitleTextColor}
            />
          </View>
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Regular};
              font-size: ${rpx(24)}px;
              color: ${theme.loginHeaderTitleTextColor};
              margin-bottom: ${rpx(12)}px;
              text-align: center;
            `}
          >
            无法连接到服务器
          </Text>
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(15)}px;
              line-height: ${rpx(22)}px;
              color: ${theme.loginHeaderSubTitleTextColor};
              text-align: center;
              margin-bottom: ${rpx(36)}px;
            `}
          >
            暂时无法连接到服务器，请检查网络连接后重试。
          </Text>
          <Button
            title={retrying ? '正在重新连接…' : '重试'}
            onPress={onRetry}
            loading={retrying}
            textStyle={{
              color: theme.buttonTextColor,
              fontSize: rpx(18),
              lineHeight: rpx(32),
            }}
            css={`
              background-color: ${theme.buttonBgColor};
              height: ${rpx(50)}px;
              border-radius: ${rpx(25)}px;
              min-width: ${rpx(200)}px;
            `}
          />
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(13)}px;
              line-height: ${rpx(20)}px;
              color: ${theme.loginHeaderSubTitleTextColor};
              text-align: center;
              margin-top: ${rpx(16)}px;
              min-height: ${rpx(20)}px;
            `}
          >
            {secondsLeft != null && secondsLeft > 0
              ? `${secondsLeft} 秒后自动重试…`
              : ''}
          </Text>
        </View>
      </SafeAreaView>
    );
  },
);
